import ReactMarkdown from "react-markdown";
import {Options} from "react-markdown/lib";
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter'
import {atomDark} from 'react-syntax-highlighter/dist/esm/styles/prism'
import React, {useCallback, useState} from "react";
import {useTranslation} from "react-i18next";
import {Link, useNavigate} from "react-router-dom";
import rehypeRaw from "rehype-raw";
import remarkGfm from 'remark-gfm'
import remarkEmbedImages from 'remark-embed-images'
import {withBase} from "../utils/basePath";

interface ProductVersionChoice {
    type: "choice_request"
    queries: {
        subject: string
        choices: string[]
    }[]

}

type ExtendedOptions = Options & {
    onVersionChange?: null | ((product: string, version: string) => void)
    onAllVersionSelected?: ((allVersions: {product: string, version: string}[]) => void)
    className?: string
}

export function TailoredMarkdown(options: ExtendedOptions) {
    const [selected, setSelected] = useState({} as { [id: string]: string })
    const {t} = useTranslation()
    const navigate = useNavigate()

    function to_version_choice(languageMatch: RegExpExecArray | null, children: any): (ProductVersionChoice | null) {
        const isJsonWithChildren = languageMatch != null && languageMatch[1] === 'json' && children != undefined;
        if (!isJsonWithChildren) {
            return null
        }
        try {
            const json_doc = JSON.parse(String(children)) as ProductVersionChoice
            if (json_doc.type != "choice_request" ||
                json_doc.queries === undefined ||
                json_doc.queries.length == 0) {
                return null
            }
            const allQueryValid = json_doc.queries.filter((query) => {
                return query.choices != undefined && query.choices.length > 0
            }).length > 0
            if (!allQueryValid) {
                return null
            }
            return json_doc
        } catch (e) {
            return null
        }
    }

    const handleVersionChange = useCallback(async (product: string, version: string, all_products: string[]) => {
        setSelected((old) => {
            const newVersions = {...old}
            if (version == "" && product in newVersions)
                delete newVersions[product]
            if (version != "")
                newVersions[product] = version
            const missingSelection = all_products.filter(p => !(p in newVersions))
            if (missingSelection.length == 0) {
                options.onAllVersionSelected?.(Object.entries(newVersions).map(([product, version]) => ({product, version})))
            }
            return newVersions
        })
        if (options.onVersionChange != null) {
            options.onVersionChange(product, version)
        }
    }, [options, setSelected])

    const {className: outerClassName, onVersionChange, ...markdownOptions} = options;

    return <div className={`w-full ${outerClassName ?? ""}`}>
    <ReactMarkdown
        rehypePlugins={[rehypeRaw]}
        remarkPlugins={[remarkGfm]}
        urlTransform={(value) => value}
        {...markdownOptions}
        components={{
            code(props) {
                const {children, className, node, ...rest} = props
                const languageMatch = /language-(\w+)/.exec(className ?? '')
                const json_doc = to_version_choice(languageMatch, children)
                const json_doc_products = json_doc?.queries.map(q => q.subject) ?? []
                if (json_doc != null) {
                    if (options.onVersionChange == null) {
                        return <>
                            {json_doc.queries.map((query) => {
                                return <div key={query.subject}>
                                <span className={"px-4"}>
                                    {query.subject}:
                                </span>
                                    <ol className={`rounded-lg px-4 text-s list-disc list-inside`}>
                                        {query.choices.map((choice: string) => {
                                            return <li key={choice}>{choice}</li>
                                        })}
                                    </ol>
                                </div>
                            })
                            }
                        </>
                    } else {
                        return <table className={`space-y-4`}>
                            <tbody>
                            {json_doc.queries.map((query, i) => {
                                return <tr key={`row_${i}`}>
                                    <td className={"px-4"}>
                                        {query.subject}
                                    </td>
                                    <td className="py-1">
                                        <select id={`version_selection_${query.subject}`}
                                                className={`bg-gray-dark-enov rounded-lg px-4 text-sm`}
                                                defaultValue={selected[query.subject]}
                                                onChange={(e) => handleVersionChange(query.subject, e.target.value, json_doc_products)}>
                                            <option value={""}></option>
                                            {query.choices.map((choice: string) => {
                                                return <option className="test-sm" key={choice} value={choice}>{choice}</option>
                                            })}
                                        </select>
                                        <span
                                            className={"text-sm px-2"}>{selected[query.subject] === undefined ? t('Select version') : t('Marked for sending')}</span>
                                    </td>
                                </tr>
                            })
                            }
                            </tbody>
                        </table>
                    }
                }
                return languageMatch ? (
                    <SyntaxHighlighter
                        language={languageMatch[1]}
                        style={atomDark}
                        wrapLongLines={true}
                    >
                        {String(children)}
                    </SyntaxHighlighter>
                ) : (
                    <code {...rest} className={className}>
                        {children}
                    </code>
                )
            },
            ol(props) {
                return <ol type={"1"} className="space-y-2 pl-8 list-decimal">{props.children}</ol>
            },
            ul(props) {
                return <ul className="space-y-2 pl-8 list-disc">{props.children}</ul>
            },
            h1(props) {
                return <h1 className="text-2xl py-4 font-bold" {...props}>{props.children}</h1>
            },
            h2(props) {
                return <h2 className="text-xl py-4 font-bold" {...props}>{props.children}</h2>
            },
            a(props) {
                const href = props.href
                const propsWithoutHref = {...props}
                delete propsWithoutHref.href
                if (href != undefined) {
                    return (
                        <Link to={href} className="text-blue-400 underline">
                            {props.children}
                        </Link>
                    )
                }
                return <a {...props}>{props.children}</a>
            },
            img(props) {
                const {src, alt} = props;
                console.log(`Handling image with src "${src?.slice(0, 10) ?? "undefined"}", prop keys: ${Object.keys(props).join(', ')}`)
                if (src == null || typeof src != 'string' || src.startsWith("http://") || src.startsWith("https://") || src.startsWith("data:")) {
                    return <img {...props} />;
                }
                const abs_src = `/internal${src.startsWith('/') ? '' : '/'}${src}`;
                const rest_props = {...props};
                delete rest_props.src;
                return <img className="py-6" src={withBase(abs_src)} alt={`${alt ?? ""}`} {...rest_props} />;
            },
            table(props) {
                return <table className="table-auto">{props.children}</table>
            },
            td(props) {
                return <td className="border border-gray-600 p-2" {...props}>{props.children}</td>
            },
            th(props) {
                return <th className="border border-gray-600 p-2" {...props}>{props.children}</th>
            },
        }}
    />
    </div>
}